import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { LangProvider } from "@/contexts/LangContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SettingsProvider } from "@/contexts/SettingsContext";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSettings from "@/pages/admin/AdminSettings";

import api from "@/lib/api";

function VisitTracker() {
  const location = useLocation();
  useEffect(() => {
    api
      .post("/visits/track", { path: location.pathname, referrer: document.referrer || "" })
      .catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  return null;
}

function Shell({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  return (
    <>
      {!isAdminRoute && <Navbar />}
      <main className={isAdminRoute ? "" : "pt-20"}>{children}</main>
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <LangProvider>
        <AuthProvider>
          <CartProvider>
            <SettingsProvider>
              <BrowserRouter>
                <VisitTracker />
                <Shell>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/tienda" element={<Shop />} />
                    <Route path="/tienda/:categorySlug" element={<Shop />} />
                    <Route path="/producto/:slug" element={<ProductDetail />} />
                    <Route path="/carrito" element={<Cart />} />
                    <Route path="/checkout/exito" element={<CheckoutSuccess />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/registro" element={<Register />} />
                    <Route path="/recuperar-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/contacto" element={<Contact />} />
                    <Route path="/legal/:slug" element={<LegalPage />} />
                    <Route
                      path="/cuenta"
                      element={
                        <ProtectedRoute>
                          <Account />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminLayout />
                        </AdminRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="productos" element={<AdminProducts />} />
                      <Route path="categorias" element={<AdminCategories />} />
                      <Route path="pedidos" element={<AdminOrders />} />
                      <Route path="contactos" element={<AdminContacts />} />
                      <Route path="ajustes" element={<AdminSettings />} />
                    </Route>
                  </Routes>
                </Shell>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    style: {
                      background: "#1c1917",
                      color: "#f8f7f5",
                      border: "1px solid #44403c",
                      fontFamily: "Manrope, sans-serif",
                      borderRadius: 0,
                    },
                  }}
                />
              </BrowserRouter>
            </SettingsProvider>
          </CartProvider>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
